import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  MapPin,
  Plus,
  Users,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getEvents } from '../api/services';
import {
  formatDate,
  formatTime,
  getApiErrorMessage,
  getDisplayName,
  slugToLabel,
} from '../utils/admin';

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'coffee', label: 'Coffee' },
  { value: 'meal', label: 'Meal' },
  { value: 'networking', label: 'Networking' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'sports', label: 'Sports' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'social', label: 'Social' },
  { value: 'professional', label: 'Professional' },
  { value: 'other', label: 'Other' },
];

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'registration_open', label: 'Registration Open' },
  { value: 'registration_closed', label: 'Registration Closed' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const eventTypeOptions = [
  { value: '', label: 'All Event Types' },
  { value: 'official', label: 'Official' },
  { value: 'user_meetup', label: 'User Meetup' },
];

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });
  const [filters, setFilters] = useState({
    status: '',
    eventType: '',
    category: '',
    city: '',
  });

  const fetchEvents = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getEvents({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.eventType && { eventType: filters.eventType }),
        ...(filters.category && { category: filters.category }),
        ...(filters.city.trim() && { city: filters.city.trim() }),
      });

      setEvents(response.data || []);
      setPagination((previous) => ({
        ...previous,
        page: response.pagination?.page || previous.page,
        limit: response.pagination?.limit || previous.limit,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 1,
      }));
    } catch (error) {
      console.error('Failed to fetch events', error);
      toast.error(getApiErrorMessage(error, 'Failed to fetch events'));
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.city, filters.eventType, filters.status, pagination.limit, pagination.page]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilterChange = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setPagination((previous) => ({
      ...previous,
      page,
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-blue-100 text-blue-800',
      registration_open: 'bg-green-100 text-green-800',
      registration_closed: 'bg-yellow-100 text-yellow-800',
      ongoing: 'bg-purple-100 text-purple-800',
      completed: 'bg-teal-100 text-teal-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOwnerName = (event) =>
    getDisplayName(
      event.eventType === 'user_meetup' ? event.hostDetails : event.createdByDetails,
      'Unknown'
    );

  const renderTimeRange = (event) =>
    `${formatTime(event.startTime)}${event.endTime ? ` - ${formatTime(event.endTime)}` : ''}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="mt-1 text-sm text-gray-500">Manage all admin-visible events, including drafts and user meetups.</p>
        </div>

        <Link
          to="/events/new"
          className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" /> Create Official Event
        </Link>
      </div>

      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Filter size={18} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <select
            value={filters.status}
            onChange={(event) => handleFilterChange('status', event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={filters.eventType}
            onChange={(event) => handleFilterChange('eventType', event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {eventTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={filters.category}
            onChange={(event) => handleFilterChange('category', event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={filters.city}
            onChange={(event) => handleFilterChange('city', event.target.value)}
            placeholder="Filter by city"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <select
            value={pagination.limit}
            onChange={(event) =>
              setPagination((previous) => ({
                ...previous,
                limit: parseInt(event.target.value, 10),
                page: 1,
              }))
            }
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
      </div>

      <div className="text-sm text-gray-600">
        {pagination.total > 0
          ? `Showing ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(
              pagination.page * pagination.limit,
              pagination.total
            )} of ${pagination.total} events`
          : 'No events found'}
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Event</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Owner</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Attendees</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-10 text-center">
                    <div className="flex justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No events found for the selected filters.
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {event.coverImage ? (
                          <img
                            src={event.coverImage}
                            alt={event.title}
                            className="h-12 w-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-16 items-center justify-center rounded-lg bg-gray-100 text-xs text-gray-500">
                            No image
                          </div>
                        )}

                        <div>
                          <p className="font-medium text-gray-900">{event.title}</p>
                          <p className="mt-1 text-xs text-gray-500">{slugToLabel(event.category)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{slugToLabel(event.eventType)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar size={14} className="mr-2 text-gray-400" />
                        {formatDate(event.date)}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{renderTimeRange(event)}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <MapPin size={14} className="mr-2 text-gray-400" />
                        {event.venue?.city || event.venue?.name || 'TBD'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getOwnerName(event)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Users size={14} className="mr-2 text-gray-400" />
                        {event.currentAttendees || 0} / {event.maxAttendees || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(event.status)}`}>
                        {slugToLabel(event.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Link
                        to={`/events/${event._id}`}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <Eye size={16} className="mr-1" /> View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && events.length > 0 && pagination.pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} className="mr-2" /> Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next <ChevronRight size={16} className="ml-2" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
